// The profanity list, apart from the screen that edits it: what counts as a
// word, how a CSV of words is read, what an import would do, and what an export
// contains.
//
// Everything here is pure and runs in the browser. The list is `string[]` in
// `games.config.chat.profanity_words` and travels with the rest of the config
// through the page's own Save — no endpoint of its own, on either side.

/**
 * The longest word the list takes.
 *
 * Not a limit the server enforces: it is the length past which a "word" is
 * certainly a pasted sentence, and `ProfanityFilter.Blocked`
 * (internal/chat/profanity.go) would never match it anyway.
 */
export const MAX_WORD_LENGTH = 64;

/**
 * Letters and digits, and nothing else.
 *
 * The reason is not cosmetic. The server tokenizes a message on non-letter,
 * non-digit boundaries and matches each token whole, so a stored "go home" or
 * "sh!t" can never equal a token — it would sit in the config forever, blocking
 * nothing. Refusing it is more honest than storing it dead.
 */
const WORD_RE = /^[\p{L}\p{N}]+$/u;

/**
 * A word as it is stored: trimmed and lowercased, because the match is
 * case-insensitive on the server and this is exactly what it compares against.
 *
 * Applied to what the operator types and to what a file brings. Words ALREADY
 * in the config are left alone — rewriting them on open would turn the first
 * look at the list into a diff nobody asked for.
 */
export function normalizeWord(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Why a word cannot go into the list, or null when it can.
 *
 * One clause, in the voice the import's problem lines are written in
 * (`line 34 «go home» — a word cannot contain spaces`); `problemSentence`
 * turns the same clause into the sentence the Add field shows.
 */
export function wordProblem(word: string): string | null {
  if (word === "") return "a word cannot be empty";
  // Asked before the general rule so the commonest mistake — a phrase pasted
  // as one entry — is answered with what is actually wrong with it.
  if (/\s/u.test(word)) return "a word cannot contain spaces";
  if (!WORD_RE.test(word)) return "a word can only contain letters and digits";
  // Counted in characters rather than UTF-16 units, so a 64-rune Cyrillic word
  // is 64 here as well.
  if ([...word].length > MAX_WORD_LENGTH) {
    return `a word cannot be longer than ${MAX_WORD_LENGTH} characters`;
  }
  return null;
}

/** The same clause as a standalone sentence, for a field's error line. */
export function problemSentence(problem: string): string {
  return `${problem.charAt(0).toUpperCase()}${problem.slice(1)}.`;
}

/** Whether the list already holds this word, ignoring case. */
export function hasWord(list: string[], word: string): boolean {
  const needle = word.toLowerCase();
  return list.some((w) => w.toLowerCase() === needle);
}

/**
 * `list` with `word` in it — appended, unless `origin` (the list as the dialog
 * opened it) already knew the word, in which case it goes back where it stood.
 *
 * Deleting a word and putting it straight back is an ordinary slip to make,
 * and appending it would leave the array re-ordered while holding exactly the
 * same words. That is the one edit this screen must not invent: the row would
 * light up --warn over a line honestly reading "none added, none removed", the
 * save bar would count a change, and the transfer's config diff would report
 * one for a list nobody actually touched.
 *
 * The seat is found by rank rather than by index, since the list has moved on
 * since: the word goes before the first entry that stood after it originally.
 * Words the origin never had rank last, so a returning word passes them.
 */
export function withWord(
  list: string[],
  word: string,
  origin: string[],
): string[] {
  const rank = new Map<string, number>();
  origin.forEach((w, i) => {
    const key = w.toLowerCase();
    if (!rank.has(key)) rank.set(key, i);
  });
  const home = rank.get(word.toLowerCase());
  if (home === undefined) return [...list, word];
  const seat = list.findIndex(
    (w) => (rank.get(w.toLowerCase()) ?? Infinity) > home,
  );
  if (seat === -1) return [...list, word];
  return [...list.slice(0, seat), word, ...list.slice(seat)];
}

// ──────────────────────────────────────────────────────────────────────────────
// Order
// ──────────────────────────────────────────────────────────────────────────────

/**
 * One collator for the whole module, so the order on screen and the order in an
 * exported file are the same order, and neither pays for a fresh Intl object
 * per comparison on a list of hundreds.
 */
const COLLATOR = new Intl.Collator();

/**
 * The list sorted for reading. The stored array keeps its own order — a
 * reordered array is a line in the transfer diff ("none added, none removed")
 * and this screen has no business writing one.
 */
export function sortWords(words: string[]): string[] {
  return [...words].sort(compareWords);
}

/**
 * The same order for a list of rows rather than of strings — the modal sorts
 * `{word, index}` pairs, because a row has to know which entry of the stored
 * array its × removes.
 */
export function compareWords(a: string, b: string): number {
  return COLLATOR.compare(a, b);
}

// ──────────────────────────────────────────────────────────────────────────────
// Reading a file
// ──────────────────────────────────────────────────────────────────────────────

/**
 * The byte order mark, escaped rather than typed: it is the one character of
 * the file nobody can see, and a literal here would be invisible in the source
 * as well.
 */
const BOM = "\uFEFF";

/**
 * The biggest file the import will read — the same 1 MB threshold, and the same
 * sentence for missing it, that the rewards import promises in its dropzone
 * (MAX_REWARDS_FILE_BYTES in features/leaderboards/rewardsFile.ts).
 */
export const MAX_WORDS_FILE_BYTES = 1 << 20;

/** Why a file over that size is turned away. */
export const WORDS_FILE_TOO_BIG =
  "This file is larger than 1 MB. A word list never is — check you picked the right one.";

/**
 * Read a picked file as text. The same three lines TransferPage and
 * rewardsFile.ts each keep for their own import — the reader is boilerplate,
 * and a shared one named after either of those two files would read wrong here.
 */
export function readWordsFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file);
  });
}

/** A cell the file offered and the list would not take. */
export interface WordsFileProblem {
  /** 1-based line of the file, so the operator can go and look at it. */
  line: number;
  /** The cell as written, before lowercasing — what they will find there. */
  raw: string;
  /** The clause from `wordProblem`. */
  reason: string;
}

export interface ParsedWordsFile {
  /** Lines the file has, trailing newline not counted. */
  linesRead: number;
  /** Valid, lowercased, de-duplicated, in the order the file lists them. */
  words: string[];
  problems: WordsFileProblem[];
}

/** One line of the skipped list, in the shape the alert prints it. */
export function problemLine(p: WordsFileProblem): string {
  return `line ${p.line} «${p.raw}» — ${p.reason}`;
}

/**
 * One line into cells, trimmed, with the quotes Excel puts around a cell taken
 * off: `"cheater"` is a spreadsheet quoting, not a word written in quotes.
 *
 * A comma INSIDE a quoted cell is not a separator. It is the only thing this
 * has to get right that a `split(",")` cannot: a legacy entry with a comma in
 * it — the ChipInput validated nothing, and the export quotes such a word —
 * would otherwise come back as two perfectly plausible words, and one bad
 * entry silently becoming two good ones is the one outcome worse than
 * refusing it.
 */
function splitCells(line: string): string[] {
  const cells: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch !== '"') {
        cur += ch;
      } else if (line[i + 1] === '"') {
        // "" inside a quoted cell is one literal quote.
        cur += '"';
        i++;
      } else {
        quoted = false;
      }
      continue;
    }
    // A quote opens a cell only where the cell starts — anywhere else it is
    // just a character in whatever is about to be refused.
    if (ch === '"' && cur.trim() === "") {
      quoted = true;
      cur = "";
      continue;
    }
    if (ch === ",") {
      cells.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  cells.push(cur.trim());
  return cells;
}

/**
 * Read a word list out of a .csv / .txt.
 *
 * Two separators, because two tools produce these files: a column, which is
 * what a spreadsheet exports, and one long comma-separated line, which is what
 * another panel's export looks like. Both are read the same way.
 *
 * A first cell reading `word` or `words` is a header somebody typed by hand,
 * and dropping it is the difference between importing a list and importing a
 * list plus the word "word".
 */
export function parseWordsFile(text: string): ParsedWordsFile {
  // A file this app exported starts with a BOM; so does anything Excel saves.
  const body = text.startsWith(BOM) ? text.slice(1) : text;
  const lines = body.split(/\r\n|\r|\n/);
  // A trailing newline ends the last line, it does not start an empty one.
  if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();

  const words: string[] = [];
  const problems: WordsFileProblem[] = [];
  const seen = new Set<string>();
  let firstCell = true;

  lines.forEach((line, i) => {
    for (const raw of splitCells(line)) {
      // Blank cells are what a column of words is padded with — they are not a
      // mistake anybody made, so they are dropped without a word about it.
      if (raw === "") continue;
      const word = normalizeWord(raw);
      if (firstCell) {
        firstCell = false;
        if (word === "word" || word === "words") continue;
      }
      const reason = wordProblem(word);
      if (reason) {
        problems.push({ line: i + 1, raw, reason });
        continue;
      }
      // A file that lists the same word twice is not a file with a problem.
      if (seen.has(word)) continue;
      seen.add(word);
      words.push(word);
    }
  });

  return { linesRead: lines.length, words, problems };
}

// ──────────────────────────────────────────────────────────────────────────────
// What an import would do
// ──────────────────────────────────────────────────────────────────────────────

export type WordsImportMode = "merge" | "replace";

export interface WordsImportPlan {
  mode: WordsImportMode;
  /** Words the file brings that the list does not have yet. */
  add: string[];
  /** Words the file brings that the list already has — Merge's "already there". */
  alreadyThere: string[];
  /** Entries of the CURRENT list the file also names — Replace's "kept". */
  kept: string[];
  /** Entries of the current list the file says nothing about — Replace drops these. */
  remove: string[];
  /** Cells the file offered and the list would not take. */
  skipped: WordsFileProblem[];
  /** The list as it would stand once the import runs. */
  result: string[];
}

/**
 * What either mode would do to `current`, and the list it would leave behind.
 *
 * Replace keeps the kept words WHERE THEY ARE and appends the new ones, rather
 * than rebuilding the array in the file's order: the list is a set as far as
 * the server is concerned, and a re-ordered array is a line in the transfer's
 * config diff for a change nobody made. Exporting a list and importing it back
 * as Replace is therefore exactly nothing.
 */
export function planWordsImport(
  current: string[],
  parsed: ParsedWordsFile,
  mode: WordsImportMode,
): WordsImportPlan {
  const incoming = new Set(parsed.words);
  const add: string[] = [];
  const alreadyThere: string[] = [];
  for (const w of parsed.words) {
    if (hasWord(current, w)) alreadyThere.push(w);
    else add.push(w);
  }
  const kept: string[] = [];
  const remove: string[] = [];
  for (const w of current) {
    if (incoming.has(w.toLowerCase())) kept.push(w);
    else remove.push(w);
  }
  return {
    mode,
    add,
    alreadyThere,
    kept,
    remove,
    skipped: parsed.problems,
    result: mode === "merge" ? [...current, ...add] : [...kept, ...add],
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Writing a file
// ──────────────────────────────────────────────────────────────────────────────

/**
 * One column, one word per row, no header.
 *
 * UTF-8 with a BOM and CRLF line endings, because the file is opened in Excel
 * more often than in anything else, and without those two Cyrillic arrives as
 * mojibake and the whole list arrives in one cell.
 */
export function wordsCsv(words: string[]): string {
  const rows = sortWords(words).map(csvCell);
  return `${BOM}${rows.join("\r\n")}${rows.length > 0 ? "\r\n" : ""}`;
}

/**
 * Quoting for the one cell that could need it. Nothing this screen writes into
 * the list can contain a comma, but a config edited through Raw JSON — or one
 * left over from the ChipInput, which validated nothing — can.
 *
 * It buys a readable file, not a round trip: such an entry is one cell in
 * Excel instead of two, and it stays whole in the import's parse — but
 * `wordProblem` turns it away all the same, and Replace then drops it. That is
 * the export being honest about a word the server could never match, and the
 * dialog says so out loud with "N skipped" and the alert beside it.
 */
function csvCell(word: string): string {
  if (/[",\r\n]/.test(word)) return `"${word.replace(/"/g, '""')}"`;
  return word;
}

/**
 * `<project slug>-profanity-<YYYY-MM-DD>.csv`, built the way
 * `snapshotFilename` (features/transfer/api.ts) builds the other file a
 * project is exported to: the name lowercased with every run of
 * non-alphanumerics collapsed to one `-`, and the date off the operator's own
 * calendar, which is the one they will look for in their downloads folder.
 *
 * A name with nothing ASCII in it slugifies to nothing, and the file is then
 * named after what it is rather than after an empty string.
 */
export function profanityFilename(
  projectName: string,
  date: Date = new Date(),
): string {
  const slug =
    projectName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "project";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${slug}-profanity-${yyyy}-${mm}-${dd}.csv`;
}

// ──────────────────────────────────────────────────────────────────────────────
// What is unsaved
// ──────────────────────────────────────────────────────────────────────────────

/**
 * How many words the draft gained and lost against the server's list, counted
 * as a multiset exactly the way `multisetDelta`
 * (internal/admin/snapshot/configdiff.go) counts it: by value, never by
 * position, so a reordering is not 814 additions and 814 removals.
 */
export function wordsDelta(
  server: string[],
  working: string[],
): { added: number; removed: number } {
  const counts = new Map<string, number>();
  for (const w of server) counts.set(w, (counts.get(w) ?? 0) + 1);
  let added = 0;
  for (const w of working) {
    const n = counts.get(w) ?? 0;
    if (n > 0) counts.set(w, n - 1);
    else added++;
  }
  let removed = 0;
  for (const n of counts.values()) removed += n;
  return { added, removed };
}

/**
 * The line under the row while the list differs from the server's, in the voice
 * the transfer's config diff uses for the same list ("+2 words, none removed" —
 * `arrayNote`). Two screens describing one change have no business disagreeing
 * about how it is worded.
 */
export function wordsChangeNote(server: string[], working: string[]): string {
  const { added, removed } = wordsDelta(server, working);
  const left = added > 0 ? `+${added} added` : "none added";
  const right = removed > 0 ? `−${removed} removed` : "none removed";
  return `${left}, ${right} — not saved yet`;
}

/** `1 word` / `814 words`, the count both the row and the modal print. */
export function wordCount(n: number): string {
  return `${n} ${n === 1 ? "word" : "words"}`;
}
